import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Stepper,
  Step,
  StepLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  LinearProgress,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Delete as DeleteIcon,
  ArrowBack as BackIcon
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import axios from 'axios';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';

const steps = ['Upload File', 'Validate Data', 'Review & Assign'];

const BulkAssetAssignment = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [validationResults, setValidationResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [previewDialog, setPreviewDialog] = useState(false);

  // Download Excel Template
  const downloadTemplate = () => {
    const template = [
      {
        'Employee ID': 'EMP001',
        'Asset Type': 'LAPTOP',
        'Asset Name': 'Dell Latitude 5420',
        'Serial Number': 'DL123456',
        'Brand': 'Dell',
        'Model': 'Latitude 5420',
        'Purchase Value': 65000,
        'Current Value': 65000,
        'Depreciation Rate': 10,
        'Assigned Date': '2025-01-01',
        'Condition': 'GOOD',
        'Notes': 'New laptop for development'
      }
    ];

    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Asset Template');
    
    // Set column widths
    ws['!cols'] = [
      { wch: 15 }, { wch: 15 }, { wch: 25 }, { wch: 15 },
      { wch: 15 }, { wch: 20 }, { wch: 15 }, { wch: 15 },
      { wch: 15 }, { wch: 15 }, { wch: 12 }, { wch: 30 }
    ];

    XLSX.writeFile(wb, 'Asset_Bulk_Assignment_Template.xlsx');
  };

  // Handle File Upload
  const handleFileUpload = (event) => {
    const uploadedFile = event.target.files[0];
    if (!uploadedFile) return;

    if (!uploadedFile.name.endsWith('.xlsx') && !uploadedFile.name.endsWith('.xls')) {
      Swal.fire('Error', 'Please upload an Excel file (.xlsx or .xls)', 'error');
      return;
    }

    setFile(uploadedFile);
    parseExcelFile(uploadedFile);
  };

  // Parse Excel File
  const parseExcelFile = (file) => {
    setLoading(true);
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        if (jsonData.length === 0) {
          Swal.fire('Error', 'Excel file is empty', 'error');
          setLoading(false);
          return;
        }

        setParsedData(jsonData);
        setActiveStep(1);
        validateData(jsonData);
      } catch (error) {
        console.error('Error parsing Excel:', error);
        Swal.fire('Error', 'Failed to parse Excel file', 'error');
      } finally {
        setLoading(false);
      }
    };

    reader.readAsArrayBuffer(file);
  };

  // Validate Data
  const validateData = async (data) => {
    setLoading(true);
    const results = [];

    const validAssetTypes = ['LAPTOP', 'MOBILE', 'TABLET', 'DESKTOP', 'VEHICLE', 'UNIFORM', 'ID_CARD', 'ACCESS_CARD', 'TOOLS', 'OTHER'];
    const validConditions = ['EXCELLENT', 'GOOD', 'FAIR', 'POOR', 'DAMAGED'];

    // Fetch all employees for validation
    let employees = [];
    try {
      const response = await axios.get('/employee/getAllEmployees');
      employees = Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Error fetching employees:', error);
    }

    for (let i = 0; i < data.length; i++) {
      const row = data[i];
      const errors = [];
      const warnings = [];

      // Validate Employee ID
      const employee = employees.find(emp => emp.emp_id === row['Employee ID']);
      if (!employee) {
        errors.push('Employee ID not found');
      }

      // Validate Asset Type
      if (!row['Asset Type'] || !validAssetTypes.includes(row['Asset Type'])) {
        errors.push('Invalid Asset Type');
      }

      // Validate Asset Name
      if (!row['Asset Name']) {
        errors.push('Asset Name is required');
      }

      // Validate Condition
      if (row['Condition'] && !validConditions.includes(row['Condition'])) {
        errors.push('Invalid Condition');
      }

      // Validate Purchase Value
      if (row['Purchase Value'] && isNaN(row['Purchase Value'])) {
        errors.push('Purchase Value must be a number');
      }

      // Validate Date
      if (row['Assigned Date']) {
        const date = new Date(row['Assigned Date']);
        if (isNaN(date.getTime())) {
          errors.push('Invalid Assigned Date');
        }
      }

      // Warnings
      if (!row['Serial Number']) {
        warnings.push('Serial Number is missing');
      }

      if (!row['Purchase Value']) {
        warnings.push('Purchase Value is missing');
      }

      results.push({
        rowNumber: i + 1,
        data: row,
        employee: employee,
        errors: errors,
        warnings: warnings,
        status: errors.length === 0 ? 'valid' : 'invalid'
      });
    }

    setValidationResults(results);
    setLoading(false);

    // Auto-advance if all valid
    const allValid = results.every(r => r.status === 'valid');
    if (allValid) {
      setActiveStep(2);
    }
  };

  // Bulk Assign Assets
  const handleBulkAssign = async () => {
    const validRows = validationResults.filter(r => r.status === 'valid');
    
    if (validRows.length === 0) {
      Swal.fire('Error', 'No valid rows to assign', 'error');
      return;
    }

    const result = await Swal.fire({
      title: 'Confirm Bulk Assignment',
      text: `Assign ${validRows.length} assets?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, Assign',
      cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) return;

    setLoading(true);
    setProgress(0);

    const results = {
      success: [],
      failed: []
    };

    for (let i = 0; i < validRows.length; i++) {
      const row = validRows[i];
      const payload = {
        employeeId: row.employee._id,
        assetType: row.data['Asset Type'],
        assetName: row.data['Asset Name'],
        serialNumber: row.data['Serial Number'] || '',
        brand: row.data['Brand'] || '',
        model: row.data['Model'] || '',
        purchaseValue: parseFloat(row.data['Purchase Value']) || 0,
        currentValue: parseFloat(row.data['Current Value']) || parseFloat(row.data['Purchase Value']) || 0,
        depreciationRate: parseFloat(row.data['Depreciation Rate']) || 0,
        assignedDate: row.data['Assigned Date'] || new Date().toISOString().split('T')[0],
        condition: row.data['Condition'] || 'GOOD',
        notes: row.data['Notes'] || ''
      };

      try {
        const response = await axios.post('/api/employee-asset/create', payload);
        if (response.data.success) {
          results.success.push({ row: row.rowNumber, data: row.data });
        } else {
          results.failed.push({ row: row.rowNumber, data: row.data, error: response.data.message });
        }
      } catch (error) {
        results.failed.push({ 
          row: row.rowNumber, 
          data: row.data, 
          error: error.response?.data?.message || error.message 
        });
      }

      setProgress(Math.round(((i + 1) / validRows.length) * 100));
    }

    setLoading(false);

    // Show results
    const successCount = results.success.length;
    const failedCount = results.failed.length;

    if (failedCount === 0) {
      Swal.fire({
        title: 'Success!',
        text: `All ${successCount} assets assigned successfully`,
        icon: 'success',
        confirmButtonText: 'OK'
      }).then(() => {
        navigate('/userdashboard/staff/hrm/assets');
      });
    } else {
      Swal.fire({
        title: 'Partial Success',
        html: `
          <p><strong>Success:</strong> ${successCount} assets</p>
          <p><strong>Failed:</strong> ${failedCount} assets</p>
          <p>Check the table for details</p>
        `,
        icon: 'warning',
        confirmButtonText: 'OK'
      });
    }
  };

  // Remove Row
  const removeRow = (index) => {
    const newResults = validationResults.filter((_, i) => i !== index);
    setValidationResults(newResults);
  };

  const getStatusColor = (status) => {
    return status === 'valid' ? 'success' : 'error';
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: '#f8fafc', minHeight: '100vh' }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <IconButton onClick={() => navigate('/userdashboard/staff/hrm/assets')}>
            <BackIcon />
          </IconButton>
          <Typography variant="h5" fontWeight={600}>Bulk Asset Assignment</Typography>
        </Box>

        {/* Stepper */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Stepper activeStep={activeStep}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Paper>

        {/* Step Content */}
        <Paper sx={{ p: 3 }}>
          {/* Step 1: Upload File */}
          {activeStep === 0 && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" gutterBottom>Upload Excel File</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Download the template, fill in asset details, and upload
              </Typography>

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 4 }}>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={downloadTemplate}
                >
                  Download Template
                </Button>
              </Box>

              <Box
                sx={{
                  border: '2px dashed #ccc',
                  borderRadius: 2,
                  p: 4,
                  bgcolor: '#fafafa',
                  cursor: 'pointer',
                  '&:hover': { bgcolor: '#f0f0f0' }
                }}
              >
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileUpload}
                  style={{ display: 'none' }}
                  id="file-upload"
                />
                <label htmlFor="file-upload" style={{ cursor: 'pointer', display: 'block' }}>
                  <UploadIcon sx={{ fontSize: 48, color: '#666', mb: 2 }} />
                  <Typography variant="body1" fontWeight={500}>
                    Click to upload or drag and drop
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Excel files only (.xlsx, .xls)
                  </Typography>
                </label>
              </Box>

              {file && (
                <Alert severity="info" sx={{ mt: 3 }}>
                  File selected: {file.name}
                </Alert>
              )}
            </Box>
          )}

          {/* Step 2: Validate Data */}
          {activeStep === 1 && (
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6">Validation Results</Typography>
                <Box>
                  <Chip 
                    label={`Valid: ${validationResults.filter(r => r.status === 'valid').length}`} 
                    color="success" 
                    sx={{ mr: 1 }} 
                  />
                  <Chip 
                    label={`Invalid: ${validationResults.filter(r => r.status === 'invalid').length}`} 
                    color="error" 
                  />
                </Box>
              </Box>

              {loading ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <LinearProgress />
                  <Typography sx={{ mt: 2 }}>Validating data...</Typography>
                </Box>
              ) : (
                <TableContainer sx={{ maxHeight: 500 }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Row</TableCell>
                        <TableCell>Employee ID</TableCell>
                        <TableCell>Asset Type</TableCell>
                        <TableCell>Asset Name</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Issues</TableCell>
                        <TableCell>Action</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {validationResults.map((result, index) => (
                        <TableRow key={index}>
                          <TableCell>{result.rowNumber}</TableCell>
                          <TableCell>{result.data['Employee ID']}</TableCell>
                          <TableCell>{result.data['Asset Type']}</TableCell>
                          <TableCell>{result.data['Asset Name']}</TableCell>
                          <TableCell>
                            <Chip 
                              label={result.status} 
                              color={getStatusColor(result.status)} 
                              size="small"
                              icon={result.status === 'valid' ? <SuccessIcon /> : <ErrorIcon />}
                            />
                          </TableCell>
                          <TableCell>
                            {result.errors.length > 0 && (
                              <Box>
                                {result.errors.map((err, i) => (
                                  <Typography key={i} variant="caption" color="error" display="block">
                                    • {err}
                                  </Typography>
                                ))}
                              </Box>
                            )}
                            {result.warnings.length > 0 && (
                              <Box>
                                {result.warnings.map((warn, i) => (
                                  <Typography key={i} variant="caption" color="warning.main" display="block">
                                    ⚠ {warn}
                                  </Typography>
                                ))}
                              </Box>
                            )}
                          </TableCell>
                          <TableCell>
                            <IconButton size="small" onClick={() => removeRow(index)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                <Button onClick={() => setActiveStep(0)}>Back</Button>
                <Button
                  variant="contained"
                  onClick={() => setActiveStep(2)}
                  disabled={validationResults.filter(r => r.status === 'valid').length === 0}
                >
                  Continue
                </Button>
              </Box>
            </Box>
          )}

          {/* Step 3: Review & Assign */}
          {activeStep === 2 && (
            <Box>
              <Typography variant="h6" gutterBottom>Review & Confirm</Typography>
              
              <Alert severity="info" sx={{ mb: 3 }}>
                {validationResults.filter(r => r.status === 'valid').length} assets ready to be assigned
              </Alert>

              {loading && (
                <Box sx={{ mb: 3 }}>
                  <LinearProgress variant="determinate" value={progress} />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                    Assigning assets... {progress}%
                  </Typography>
                </Box>
              )}

              <TableContainer sx={{ maxHeight: 400, mb: 3 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Employee</TableCell>
                      <TableCell>Asset Type</TableCell>
                      <TableCell>Asset Name</TableCell>
                      <TableCell>Serial Number</TableCell>
                      <TableCell>Value</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {validationResults.filter(r => r.status === 'valid').map((result, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          {result.employee?.staff_name} ({result.data['Employee ID']})
                        </TableCell>
                        <TableCell>{result.data['Asset Type']}</TableCell>
                        <TableCell>{result.data['Asset Name']}</TableCell>
                        <TableCell>{result.data['Serial Number'] || 'N/A'}</TableCell>
                        <TableCell>₹{result.data['Purchase Value']?.toLocaleString() || 0}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Button onClick={() => setActiveStep(1)} disabled={loading}>Back</Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleBulkAssign}
                  disabled={loading}
                >
                  {loading ? 'Assigning...' : 'Assign All Assets'}
                </Button>
              </Box>
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default BulkAssetAssignment;
